import type { components } from 'store/apis/permissionsApi/generated/permissions-api';

/**
 * The permission vocabulary.
 *
 * Action names are `permissions-service`'s, adopted before the service is called so that switching
 * to it later is a change behind the port rather than a rename at every call site (HNS-323).
 *
 * `natter:*` covers every event kind - hosted natters, open natters and Natalie. Create and edit
 * both collapse into `natter:manage`.
 */

/**
 * The published catalogue, pinned to the service's own contract.
 *
 * Names come from the vendored spec at `api-specs/permissions.openapi.yaml`, which is authoritative
 * over the README (ADR-0006). The assertions below fail `type-check` when this list and the spec
 * disagree, so a rename upstream is a build error here rather than a 400 once the adapter is
 * swapped.
 *
 * The spec is the contract, not the deployment: `GET /v1/actions` serves the live catalogue, needs
 * SigV4, and has still not been read, so a service running behind its own spec would not show up
 * here. The U1 probe on HNS-323 owns that gap.
 */
export const CATALOGUE_ACTIONS = [
  'organisation:view',
  'organisation:manage',
  'community:view',
  'community:manage',
  'community:setEntitlements',
  'natter:view',
  'natter:participate',
  'natter:manage',
  'report:view',
  'report:manage',
] as const;

/**
 * Actions this app needs that the catalogue cannot express. Both are quarantined here rather than
 * folded into a coarser catalogue action, because folding either one would widen access:
 *
 * - `natter:authorReport` - a soft-launch gate for Natalie authoring wearing an identity flag,
 *   not a permanent permission. It is derived from `isOrganizationMember`, which is true for a
 *   membership row of any role, so it admits every organisation admin rather than Natter staff.
 *   It is still tighter than community admin, who may READ reports but not write them, and than
 *   natalie-api's `requireCommunityAdmin`: a product rule, not an API mirror, so a looser API is
 *   expected rather than a bug. The nearest catalogue action, `report:manage`, permits
 *   `communityAdminOf || orgAdminOf || context.owner`, so mapping to it would hand community
 *   admins and event owners the capability the rule withholds. HNS-349 owns its exit criteria.
 *
 *   It covers **authoring a Natalie session as well as its report** - one Natalie surface, one
 *   audience, which is why the create button, the report row menu and the event row menu cannot be
 *   allowed to drift apart. The name is therefore narrower than the rule it carries; `natter:manage`
 *   would read better at the session sites but is a widening in two directions (community admins
 *   hold `CREATE_EVENT`, and its `scopedOrIsOwned` rule lets an owner in), so those sites ask this
 *   action and say so. HNS-349 question 5 owns the reconciliation.
 *
 *   Account-level, so it needs no active-community reconciliation: it is derived from
 *   `isOrganizationMember` with no community input. That was deliberate, to let staff keep the
 *   capability in a community they do not belong to - and under what the flag now means, it lets
 *   an admin of one organisation keep it in another organisation's community. The service models
 *   the same predicate as `orgMemberOf` and scopes it per organisation, so HNS-349 can close this
 *   by tightening rather than by adding an action.
 * - `natter:operate` - starting and ending an event is host-only, tighter than `natter:manage`,
 *   which also admits community admins and event owners.
 *
 * Each needs either an action in the service or an explicit product decision. HNS-349 owns closing
 * these catalogue gaps. Adding an action to the service is non-breaking per its ADR-0007;
 * renaming one is not, which is why these carry a distinct prefix rather than squatting on a
 * plausible catalogue name.
 */
export const LOCAL_ACTIONS = ['natter:authorReport', 'natter:operate'] as const;

export type CatalogueAction = (typeof CATALOGUE_ACTIONS)[number];
export type LocalAction = (typeof LOCAL_ACTIONS)[number];
export type Action = CatalogueAction | LocalAction;

/**
 * The grant list the service serves us, read off its spec rather than restated.
 *
 * Deliberately `MeResponse.allowed` and not the `/v1/actions` catalogue, even though the spec calls
 * the latter authoritative: this app only ever reads the former, so it is the union a drift would
 * actually reach us through. The spec declares the same enum in three places, and pinning against a
 * copy we never parse would fail our build over an upstream inconsistency we neither suffer from
 * nor can fix.
 */
type ServedAction = components['schemas']['MeResponse']['allowed'][number];

/** `T` must be `true`. A `false` here is a catalogue drift; the alias name says which direction. */
type Assert<T extends true> = T;

/**
 * Both directions, because they fail differently. An action the service serves that this list omits
 * is a grant silently dropped by `transformResponse` - a capability the user holds and does not get.
 * One this list carries that the service no longer serves is an ordinary denial today, and a
 * rename's tell: the old name stops appearing while the new one is discarded as unknown (R20).
 */
type _EveryServedActionIsInTheCatalogue = Assert<ServedAction extends CatalogueAction ? true : false>;
type _EveryCatalogueActionIsServed = Assert<CatalogueAction extends ServedAction ? true : false>;

/** Every action, so a test can pin the whole vocabulary and a new one cannot slip in unnoticed. */
export const ALL_ACTIONS: readonly Action[] = [...CATALOGUE_ACTIONS, ...LOCAL_ACTIONS];

/**
 * Whether the service knows this action. The adapter that talks to `permissions-service` must not
 * send it a local action, which would be a 400.
 */
export const isCatalogueAction = (action: Action): action is CatalogueAction =>
  (CATALOGUE_ACTIONS as readonly string[]).includes(action);
